import { api } from "@/redux/api";

import { NotificationFrequency } from "../../pages/Notifications/enum/notification.enum";
import {
	NotificationGroupDefinitionsResponse,
	NotificationGroupDefinitionsResponseSchema,
	NotificationPreference,
	NotificationPreferenceSchema,
	NotificationPreferencesResponse,
	NotificationPreferencesResponseSchema,
	UpsertNotificationPreference,
} from "../../pages/Notifications/schema/notification";

export const notificationPreferenceApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getAvailableGroups: builder.query<NotificationGroupDefinitionsResponse, void>({
			query: () => ({
				url: "notification-preferences/available",
				responseSchema: NotificationGroupDefinitionsResponseSchema,
			}),
			providesTags: ["NotificationPreferences"],
		}),
		getPreferences: builder.query<NotificationPreferencesResponse, void>({
			query: () => ({
				url: "notification-preferences",
				responseSchema: NotificationPreferencesResponseSchema,
			}),
			providesTags: ["NotificationPreferences"],
		}),
		getPreference: builder.query<NotificationPreference | null, string>({
			query: (groupKey) => ({
				url: `notification-preferences/${groupKey}`,
				responseSchema: NotificationPreferenceSchema.nullable(),
			}),
			providesTags: ["NotificationPreferences"],
		}),
		upsertPreference: builder.mutation<NotificationPreference, UpsertNotificationPreference>({
			query: (body) => ({
				url: "notification-preferences",
				method: "PUT",
				body,
				responseSchema: NotificationPreferenceSchema,
			}),
			invalidatesTags: ["NotificationPreferences"],
			async onQueryStarted(body, { dispatch, queryFulfilled }) {
				const patch = dispatch(
					notificationPreferenceApi.util.updateQueryData("getPreferences", undefined, (draft) => {
						const idx = draft.findIndex((p) => p.eventType === body.groupKey);

						if (idx !== -1) {
							Object.assign(draft[idx], {
								...body,
								eventType: body.groupKey,
							});
						} else {
							// Create a temporary optimistic entry
							draft.push({
								id: -Date.now(),
								userId: 0,
								companyId: null,
								thresholds: null,
								createdAt: new Date().toISOString(),
								updatedAt: new Date().toISOString(),
								eventType: body.groupKey,
								appEnabled: body.appEnabled ?? true,
								emailEnabled: body.emailEnabled ?? true,
								digestEnabled: body.digestEnabled ?? true,
								frequency: body.frequency ?? NotificationFrequency.INSTANT,
							});
						}
					})
				);

				try {
					await queryFulfilled;
				} catch {
					patch.undo();
				}
			},
		}),
		deletePreference: builder.mutation<{ success: boolean }, string>({
			query: (groupKey) => ({
				url: `notification-preferences/${groupKey}`,
				method: "DELETE",
			}),
			invalidatesTags: ["NotificationPreferences"],
			async onQueryStarted(groupKey, { dispatch, queryFulfilled }) {
				const patch = dispatch(
					notificationPreferenceApi.util.updateQueryData("getPreferences", undefined, (draft) => {
						const idx = draft.findIndex((p) => p.eventType === groupKey);

						if (idx !== -1) {
							draft.splice(idx, 1);
						}
					})
				);

				try {
					await queryFulfilled;
				} catch {
					patch.undo();
				}
			},
		}),
	}),
});

export const {
	useGetAvailableGroupsQuery,
	useGetPreferencesQuery,
	useGetPreferenceQuery,
	useUpsertPreferenceMutation,
	useDeletePreferenceMutation,
} = notificationPreferenceApi;
